<template>
  <div class="page-container">
    <div class="page-title-container">
      <h1 class="page-title">Registro masivo de movimientos</h1>
    </div>

    <div>
      <wz-card style="max-width: 900px" :body-padding="false">
        <template #header>
          <div class="wz-card-title">
            <span v-if="currentStep === 1">Paso 1: Concepto y monto</span>
            <span v-if="currentStep === 2">Paso 2: Seleccionar colaboradores</span>
            <span v-if="currentStep === 3">Paso 3: Confirmar y registrar</span>
          </div>
        </template>
          <section class="form-section mb-0">

            <!-- PASO 1 -->
            <div v-if="currentStep === 1">
              <div class="flex flex-wrap -mx-2">

                <!-- Concepto -->
                <div class="w-full px-2">
                  <div class="mb-3">
                    <label>Concepto <span class="text-danger">*</span></label>
                    <wz-select
                      :items="conceptOptions"
                      v-model="bulkForm.conceptId"
                      placeholder="Seleccionar concepto"
                      @change="onConceptSelected"
                    />
                    <span v-if="step1Errors.conceptId" class="wz-form-error-msg"><i class="ri-error-warning-line"></i>El concepto es requerido</span>
                    <div v-if="selectedConcept" class="mt-2">
                      <wz-badge class="mr-2" :variant="selectedConcept.type === 'DESCUENTO' ? 'danger-solid' : 'success-solid'">
                        {{ selectedConcept.type === 'DESCUENTO' ? 'Descuento' : 'Adicional' }}
                      </wz-badge>
                      <span class="text-muted text-sm">{{ getNatureLabel(selectedConcept.nature) }}</span>
                    </div>
                  </div>
                </div>

                <!-- Período: Mes -->
                <div class="w-full md:w-1/3 px-2">
                  <div class="mb-3">
                    <label>Mes <span class="text-danger">*</span></label>
                    <wz-select :items="monthOptions" v-model="bulkForm.periodMonth" defaultOption="Seleccione mes" />
                    <span v-if="step1Errors.periodMonth" class="wz-form-error-msg"><i class="ri-error-warning-line"></i>El mes es requerido</span>
                  </div>
                </div>

                <!-- Período: Año -->
                <div class="w-full md:w-1/3 px-2">
                  <div class="mb-3">
                    <label>Año <span class="text-danger">*</span></label>
                    <wz-select :items="yearOptions" v-model="bulkForm.periodYear" defaultOption="Seleccione año" />
                    <span v-if="step1Errors.periodYear" class="wz-form-error-msg"><i class="ri-error-warning-line"></i>El año es requerido</span>
                  </div>
                </div>

                <!-- Moneda -->
                <div class="w-full md:w-1/3 px-2">
                  <div class="mb-3">
                    <label>Moneda</label>
                    <wz-select :items="currencyOptions" v-model="bulkForm.currency" />
                  </div>
                </div>

                <!-- Monto -->
                <div class="w-full md:w-1/2 px-2">
                  <div class="mb-3">
                    <label>Monto <span class="text-danger">*</span></label>
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      v-model="bulkForm.amount"
                      placeholder="0.00"
                      class="wz-form-control"
                    />
                    <span v-if="step1Errors.amount" class="wz-form-error-msg"><i class="ri-error-warning-line"></i>El monto debe ser mayor a 0</span>
                  </div>
                </div>

                <!-- Descripción -->
                <div class="w-full px-2">
                  <div class="mb-3">
                    <label>Descripción</label>
                    <textarea v-model="bulkForm.description" rows="2" placeholder="Descripción opcional" class="wz-form-control"></textarea>
                  </div>
                </div>

              </div>
              <div class="mb-3 text-right text-right">
                <wz-button variant="light" @click="$router.push({ name: 'payroll-movement-list' })">Cancelar</wz-button>
                <wz-button variant="primary" class="ml-2" @click="goStep2">Siguiente</wz-button>
              </div>
            </div>

            <!-- PASO 2 -->
            <div v-if="currentStep === 2">
              <!-- Search filter -->
              <div class="mb-3">
                <input
                  type="text"
                  v-model="employeeSearch"
                  placeholder="Buscar por nombre o documento"
                  class="wz-form-control"
                  style="max-width: 400px"
                />
              </div>

              <div class="flex items-center mb-2">
                <span class="mr-3 text-muted">{{ selectedEmployeeIds.length }} colaborador(es) seleccionado(s)</span>
                <wz-button variant="light" size="sm" class="mr-2" @click="selectAll">Seleccionar todos</wz-button>
                <wz-button variant="light" size="sm" @click="deselectAll">Deseleccionar todos</wz-button>
              </div>

              <div class="overflow-x-auto">
                <table class="wz-table w-full px-2">
                  <thead>
                    <tr>
                      <th style="width: 40px"></th>
                      <th>Colaborador</th>
                      <th>Documento</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="filteredEmployees.length === 0">
                      <td colspan="3" class="text-center text-muted py-4">No se encontraron colaboradores</td>
                    </tr>
                    <tr v-for="emp in filteredEmployees" :key="emp.key" style="cursor:pointer" @click="toggleEmployee(emp.key)">
                      <td>
                        <input type="checkbox" :checked="selectedEmployeeIds.includes(emp.key)" @click.stop="toggleEmployee(emp.key)" />
                      </td>
                      <td>{{ emp.name }}</td>
                      <td>{{ emp.identifier }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <span v-if="step2Error" class="wz-form-error-msg"><i class="ri-error-warning-line"></i>Debe seleccionar al menos un colaborador</span>

              <div class="mb-3 text-right text-right mt-3">
                <wz-button variant="light" class="mr-2" @click="currentStep = 1">Anterior</wz-button>
                <wz-button variant="primary" @click="goStep3">Siguiente</wz-button>
              </div>
            </div>

            <!-- PASO 3 -->
            <div v-if="currentStep === 3">
              <wz-alert variant="info">
                <strong>Resumen del registro masivo</strong><br />
                <div class="mt-2">
                  <span>Concepto: <strong>{{ selectedConcept ? selectedConcept.name : '-' }}</strong></span>
                  <wz-badge v-if="selectedConcept" class="ml-2" :variant="selectedConcept.type === 'DESCUENTO' ? 'danger-solid' : 'success-solid'">
                    {{ selectedConcept.type === 'DESCUENTO' ? 'Descuento' : 'Adicional' }}
                  </wz-badge>
                </div>
                <div>Monto: <strong>{{ bulkForm.currency }} {{ Number(bulkForm.amount).toLocaleString('es-PE', { minimumFractionDigits: 2 }) }}</strong></div>
                <div>Período: <strong>{{ getMonthName(bulkForm.periodMonth) }} {{ bulkForm.periodYear }}</strong></div>
                <div>Colaboradores: <strong>{{ selectedEmployeeIds.length }}</strong></div>
              </wz-alert>

              <!-- Result after save -->
              <div v-if="bulkResult" class="mt-3">
                <wz-alert :variant="bulkResult.errors > 0 ? 'warning' : 'success'">
                  <strong>{{ bulkResult.processed }}</strong> movimiento(s) registrado(s).
                  <span v-if="bulkResult.errors > 0"><strong>{{ bulkResult.errors }}</strong> error(es).</span>
                </wz-alert>
                <ul v-if="bulkResult.errorMessages && bulkResult.errorMessages.length" class="list-unstyled">
                  <li v-for="(msg, idx) in bulkResult.errorMessages" :key="idx" class="text-danger text-sm">
                    <i class="ri-error-warning-line mr-1"></i>{{ msg }}
                  </li>
                </ul>
              </div>

              <div class="mb-3 text-right text-right mt-3">
                <wz-button v-if="!bulkResult" variant="light" class="mr-2" @click="currentStep = 2">Anterior</wz-button>
                <wz-button
                  v-if="!bulkResult"
                  variant="primary"
                  @click="saveBulk"
                >
                  Confirmar y registrar
                </wz-button>
                <wz-button v-if="bulkResult" variant="primary" @click="$router.push({ name: 'payroll-movement-list' })">
                  Ver lista
                </wz-button>
              </div>
            </div>

          </section>
      </wz-card>
    </div>
  </div>
</template>

<script>
import { Alert } from "@wizepe/wizeui"
import { PayrollMovementService, PayrollConceptService, EmployeeService } from '@/core/services'
import { Constants as LocalConstants } from '@/core/utils'

const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
]

export default {
  name: 'PayrollMovementBulk',
  

  data() {
    const now = new Date()
    return {
      currentStep:     1,
      conceptOptions:  [],
      allConcepts:     [],
      selectedConcept: null,
      allEmployees:    [],
      employeeSearch:  '',
      selectedEmployeeIds: [],
      bulkResult:      null,
      step1Errors:     { conceptId: false, periodMonth: false, periodYear: false, amount: false },
      step2Error:      false,
      monthOptions: MONTH_NAMES.map((m, i) => ({ key: i + 1, label: m })),
      currencyOptions: [
        { key: 'PEN', label: 'PEN - Soles' },
        { key: 'USD', label: 'USD - Dólares' },
      ],
      bulkForm: {
        conceptId:   null,
        periodMonth: now.getMonth() + 1,
        periodYear:  now.getFullYear(),
        currency:    'PEN',
        amount:      null,
        description: null,
      },
    }
  },

  computed: {
    yearOptions() {
      const current = new Date().getFullYear()
      return Array.from({ length: 5 }, (_, i) => ({ key: current - 2 + i, label: String(current - 2 + i) }))
    },
    filteredEmployees() {
      if (!this.employeeSearch) return this.allEmployees
      const q = this.employeeSearch.toLowerCase()
      return this.allEmployees.filter(e =>
        (e.name || '').toLowerCase().includes(q) ||
        (e.identifier || '').toLowerCase().includes(q)
      )
    },
  },

  methods: {
    onConceptSelected(id) {
      this.bulkForm.conceptId  = id
      this.selectedConcept     = this.allConcepts.find(c => c.id === id) || null
    },

    getNatureLabel(nature) {
      const n = LocalConstants.PayrollConceptNature[nature]
      return n ? n.label : nature
    },

    getMonthName(month) {
      return MONTH_NAMES[(month || 1) - 1] || ''
    },

    goStep2() {
      this.step1Errors = {
        conceptId:   !this.bulkForm.conceptId,
        periodMonth: !this.bulkForm.periodMonth,
        periodYear:  !this.bulkForm.periodYear,
        amount:      !this.bulkForm.amount || Number(this.bulkForm.amount) <= 0,
      }
      if (Object.values(this.step1Errors).some(Boolean)) return
      this.currentStep = 2
    },

    goStep3() {
      this.step2Error = this.selectedEmployeeIds.length === 0
      if (this.step2Error) return
      this.currentStep = 3
    },

    toggleEmployee(id) {
      const idx = this.selectedEmployeeIds.indexOf(id)
      if (idx === -1) this.selectedEmployeeIds.push(id)
      else this.selectedEmployeeIds.splice(idx, 1)
    },

    selectAll() {
      this.selectedEmployeeIds = this.filteredEmployees.map(e => e.key)
    },

    deselectAll() {
      this.selectedEmployeeIds = []
    },

    async saveBulk() {
      try {
        await this.$store.dispatch('app/loading', true)
        const data = {
          conceptId:   this.bulkForm.conceptId,
          periodYear:  this.bulkForm.periodYear,
          periodMonth: this.bulkForm.periodMonth,
          amount:      Number(this.bulkForm.amount),
          currency:    this.bulkForm.currency,
          description: this.bulkForm.description || null,
          employeeIds: this.selectedEmployeeIds,
        }
        const resp = await PayrollMovementService.createBulk(data)
        this.bulkResult = resp.payload
        if (this.bulkResult.errors === 0) {
          Alert.success(`${this.bulkResult.processed} movimiento(s) registrado(s) correctamente`)
        }
      } catch (ex) {
        Alert.error(ex.errors ? ex.errors.message : 'Error al registrar los movimientos')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
  },

  async created() {
    try {
      const [conceptsResp, employeesResp] = await Promise.all([
        PayrollConceptService.listActive(),
        EmployeeService.listAll(),
      ])
      this.allConcepts     = conceptsResp.payload || []
      this.conceptOptions  = this.allConcepts.map(c => ({ key: c.id, label: `${c.code} - ${c.name}` }))
      this.allEmployees    = (employeesResp.payload || []).map(e => ({
        key:        e.id,
        name:       e.description,
        identifier: e.identifier || '',
      }))
    } catch (e) {
      console.error('Error al cargar catálogos', e)
    }
  },
}
</script>

<style lang="stylus" scoped></style>
